import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext();

// ── Demo-User für Fallback ohne Backend ──────────────────────
const DEMO_USER = {
  id: 'user-001',
  firstName: 'Max',
  lastName: 'Mustermann',
  username: 'max.mustermann',
  email: 'max@helpbook.de',
  phone: '0171 1234567',
  postalCode: '10115',
  avatar: 'M',
  helpCount: 12,
  rating: 4.8,
  isOnline: true,
  lastSeen: new Date().toISOString(),
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(false);

  useEffect(() => {
    // Check for existing auth on mount
    const token = localStorage.getItem('helpbook_token');
    const savedUser = localStorage.getItem('helpbook_user');

    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
        setIsDemo(token.startsWith('demo-token-'));
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('helpbook_token');
        localStorage.removeItem('helpbook_user');
      }
    }

    setLoading(false);
  }, []);

  const login = async (contact, password) => {
    try {
      const response = await authAPI.login(contact, password);
      const { token, user: userData } = response.data;

      localStorage.setItem('helpbook_token', token);
      localStorage.setItem('helpbook_user', JSON.stringify(userData));
      setUser(userData);
      setIsDemo(false);

      return { success: true, user: userData };
    } catch (error) {
      // ── MOCK FALLBACK: Kein Backend? → Demo-Login ─────────
      console.warn('⚠️ Backend nicht erreichbar – Demo-Modus aktiv');
      const mockUser = {
        ...DEMO_USER,
        email: contact || DEMO_USER.email,
        lastSeen: new Date().toISOString(),
      };
      const mockToken = 'demo-token-' + Date.now();
      localStorage.setItem('helpbook_token', mockToken);
      localStorage.setItem('helpbook_user', JSON.stringify(mockUser));
      setUser(mockUser);
      setIsDemo(true);
      return { success: true, user: mockUser, demo: true };
    }
  };

  const register = async (userData) => {
    try {
      const response = await authAPI.register(userData);
      const { token, user: newUser } = response.data;

      localStorage.setItem('helpbook_token', token);
      localStorage.setItem('helpbook_user', JSON.stringify(newUser));
      setUser(newUser);
      setIsDemo(false);

      return { success: true, user: newUser };
    } catch (error) {
      // ── MOCK FALLBACK ──────────────────────────────────────
      console.warn('⚠️ Backend nicht erreichbar – Demo-Registrierung');
      const mockUser = {
        ...DEMO_USER,
        firstName: userData.firstName || DEMO_USER.firstName,
        lastName: userData.lastName || DEMO_USER.lastName,
        username: userData.username || DEMO_USER.username,
        email: userData.email || DEMO_USER.email,
        phone: userData.phone || '',
        postalCode: userData.postalCode || '',
        helpCount: 0,
        lastSeen: new Date().toISOString(),
      };
      const mockToken = 'demo-token-' + Date.now();
      localStorage.setItem('helpbook_token', mockToken);
      localStorage.setItem('helpbook_user', JSON.stringify(mockUser));
      setUser(mockUser);
      setIsDemo(true);
      return { success: true, user: mockUser, demo: true };
    }
  };

  const logout = () => {
    localStorage.removeItem('helpbook_token');
    localStorage.removeItem('helpbook_user');
    setUser(null);
    setIsDemo(false);
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('helpbook_user', JSON.stringify(updatedUser));
  };

  const isAuthenticated = () => {
    return !!user && !!localStorage.getItem('helpbook_token');
  };

  const value = {
    user,
    loading,
    isDemo,
    login,
    register,
    logout,
    updateUser,
    isAuthenticated,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
